/* eslint-disable object-curly-newline,no-nested-ternary,no-underscore-dangle */
import React, { useEffect, memo, useRef } from 'react';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  FormControl, FormHelperText, IconButton,
  InputLabel,
  MenuItem,
  TextField, Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BrokerDatePicker from '../../../../common/BrokerDatePicker';
import OutlinedSelect from '../../../../common/OutlinedSelect';
import CustomPanel from '../../../../common/CustomPanel';
import MedicalConditions from './MedicalConditions';
import { requestDependentIdentification } from '../actions/dependents_identification';
import { isValidId } from '../../../../../utils/id-validator';
import {
  selectGender,
  selectCatalog,
  selectRelationship,
  selectTypeIdentification, selectDivisionPolitica,
} from '../utils/selectors';
import * as Constants from '../../../../../utils/Constants';
import { addSweetAlert, closeSweetAlert } from '../../../../common/SweetAlert/actions/sweet_alert';
import {
  isEmpty,
  resetFormApplicationValues,
  shouldBlockFields,
  usePrevious,
} from '../../../../../utils/CommonUtils';
import { PICKER_FORMAT_DATE } from '../../../../../utils/Constants';
import Covid19Questions from './Covid19Questions';
import { ResetApplicationForm } from '../../Payment/Enums';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
  },
  radioGroup: {
    marginTop: 10,
  },
}));

function Dependents({ dependants = [], isEnabledCovidParam, editionMode, productId }) {
  const getApplication = useSelector((state) => state.getApplication);
  const prevGetApplication = usePrevious(getApplication);
  const classes = useStyles();
  const dependentsIdentification = useSelector((state) => state.dependentsIdentification);
  const roleSelector = useSelector(selectCatalog('Roles'));
  const typeIdentificationSelector = useSelector(selectTypeIdentification);
  const gens = useSelector(selectGender);
  const relationshipSelector = useSelector(selectRelationship);
  const dispatch = useDispatch();
  const actualIndex = useRef(-1);
  const [dependantsValues, setDependantsValues] = React.useState([]);
  const countries = useSelector(selectDivisionPolitica);
  const [enableCovidMedicalCondition, setEnableCovidMedicalCondition] = React.useState([]);
  const [isDisableInputs, setIsDisableInputs] = React.useState([]);

  const formMethods = useFormContext();
  const formErrors = formMethods.formState?.errors;
  const { fields, append } = useFieldArray(
    {
      control: formMethods.control,
      name: 'insuranceDependants',
    },
  );

  // Update array
  const hasCovid = (areAnswersRight, itemIndex) => {
    setEnableCovidMedicalCondition(
      enableCovidMedicalCondition.map((item, index) => (index === itemIndex
        ? { ...item, hasCovidEnabled: areAnswersRight }
        : item)),
    );
  };

  // Create empty array
  useEffect(() => {
    // Default Initializer
    if (dependants.length > 0) {
      dependants.map((item) => {
        setEnableCovidMedicalCondition((prevArray) => [...prevArray, { hasCovidEnabled: false }]);
      });
    }
  }, [dependants]);

  useEffect(() => {
    if (prevGetApplication && (prevGetApplication.response !== getApplication.response)) {
      if (!isEmpty(getApplication.response)) {
        setDependantsValues(getApplication.response.InsuranceApplicationDependants);
      }
    }
  }, [getApplication.response]);

  useEffect(() => {
    if (dependantsValues?.length > 0) {
      setValuesOnDependantsForm(dependantsValues);
      // Initializer on EditMode
      setEnableCovidMedicalCondition(
        enableCovidMedicalCondition
          .map((item, index) => (dependantsValues[index]?.QuestionCovid19ResultPositive
            ? { ...item, hasCovidEnabled: dependantsValues[index]?.QuestionCovid19ResultPositive }
            : item)),
      );
    }
  }, [dependantsValues]);

  const setValuesOnDependantsForm = (insuranceApplicationDependants) => {
    insuranceApplicationDependants.forEach((item, index) => {
      formMethods.setValue(`insuranceDependants[${index}].identificationType`, item.Person.IdentificationType.trim() || '');
      formMethods.setValue(`insuranceDependants[${index}].identification`, item.Person.Identification.trim());
      formMethods.setValue(`insuranceDependants[${index}].name`, item.Person.Name.trim() || '');
      formMethods.setValue(`insuranceDependants[${index}].lastName`, item.Person.LastName.trim() || '');
      formMethods.setValue(`insuranceDependants[${index}].gender`, item.Person.Gender.trim() || '');
      formMethods.setValue(`insuranceDependants[${index}].birthDate`, item.Person.BirthDate.trim() || '');
      formMethods.setValue(`insuranceDependants[${index}].birthPlace`, item.Person.BirthPlace.trim() || '');
      formMethods.setValue(`insuranceDependants[${index}].nacionality`, item.Person.Nacionality);
      formMethods.setValue(`insuranceDependants[${index}].questionCovid19TestPerformed`, item?.QuestionCovid19TestPerformed);
      formMethods.setValue(`insuranceDependants[${index}].height`, item?.Height);
      formMethods.setValue(`insuranceDependants[${index}].weight`, item?.Weight);
    });
  };

  useEffect(() => {
    const fieldsArray = {
      identificationType: '',
      relationshipBeneficiary: '',
      medicalConditions: {},
      identification: '',
      name: '',
      birthDate: null,
      birthPlace: '',
      gender: '',
      height: '',
      weight: ''
    };
    append(Array.from({ length: dependants.length }, () => fieldsArray));
    setIsDisableInputs(Array.from({ length: dependants.length }, () => ({ isDisable: true })));
  }, [append, dependants.length]);


  useEffect(() => {
    if (editionMode) {
      setIsDisableInputs(dependantsValues.map((_, index) => ({ isDisable:
        dependantsValues[index].Person.IsValidatedCivilRegistry })));
    }
  }, [editionMode, dependantsValues]);

  function onChangeIsDisableInputs(dependents) {
    setIsDisableInputs(isDisableInputs.map((item, index) => actualIndex.current === index
      ? { isDisable:
          shouldBlockFields(dependents[actualIndex.current].response) }
      : item));
  }

  useEffect(() => {
    const { dependents } = dependentsIdentification;
    if (actualIndex.current >= 0) {
      //onChangeIsDisableInputs(dependents); //se deshabilita para que no puedan cambiar los datos
      if (dependents[actualIndex.current].found) {
        formMethods.clearErrors([
          `insuranceDependants[${actualIndex.current}].identification`,
          `insuranceDependants[${actualIndex.current}].name`,
          `insuranceDependants[${actualIndex.current}].lastName`,
          `insuranceDependants[${actualIndex.current}].birthDate`,
          `insuranceDependants[${actualIndex.current}].gender`,
          `insuranceDependants[${actualIndex.current}].birthPlace`,
          `insuranceDependants[${actualIndex.current}].nacionality`,
          `insuranceDependants[${actualIndex.current}].height`,
          `insuranceDependants[${actualIndex.current}].weight`,
        ]);
        const { validationResult } = dependents[actualIndex.current]?.response;
        if (validationResult?.result !== 'SUCCESS') {
          const { result } = validationResult;
          if (result === 'FINISH_SALE_PROCESS' || result === 'AGE_NOT_ALLOWED' || result === 'DISABLED_PERSON'  || result === 'CONTINUE_WITH_CLIENT_WARNING' || result === 'INACTIVATE_PERSON') {
            const type = result === 'CONTINUE_WITH_CLIENT_WARNING' ? 'info' : 'warning';
            const title = result === 'CONTINUE_WITH_CLIENT_WARNING' ? 'Documento no válido' : '';
            dispatch(addSweetAlert({
              type,
              title,
              content: (
                  <Grid
                      container
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      spacing={3}
                      style={{ marginTop: 10 }}
                  >
                    <Typography
                        variant="h6"
                    >
                      {`${validationResult.messages[0]}`}
                    </Typography>
                  </Grid>
              ),
              showCancel: false,
              onConfirmLabel: 'Aceptar',
              onConfirm: () => {
               // resetFormApplication();
                dispatch(closeSweetAlert());
              },
            }));
          }
        } else {
          formMethods.setValue(`insuranceDependants[${actualIndex.current}].name`, dependents[actualIndex.current].response?.personDto.name || '');
          formMethods.setValue(`insuranceDependants[${actualIndex.current}].lastName`, dependents[actualIndex.current].response?.personDto.lastName || '');
          formMethods.setValue(`insuranceDependants[${actualIndex.current}].birthDate`, dependents[actualIndex.current].response?.personDto.birthDate || null);
          formMethods.setValue(`insuranceDependants[${actualIndex.current}].gender`, dependents[actualIndex.current].response?.personDto.gender || '');
          formMethods.setValue(`insuranceDependants[${actualIndex.current}].birthPlace`, dependents[actualIndex.current].response?.personDto.birthPlace || '');
          formMethods.setValue(`insuranceDependants[${actualIndex.current}].nacionality`, dependents[actualIndex.current].response?.personDto.nacionality);
          formMethods.setValue(`insuranceDependants[${actualIndex.current}].height`, dependents[actualIndex.current].response?.personDto.height);
          formMethods.setValue(`insuranceDependants[${actualIndex.current}].weight`, dependents[actualIndex.current].response?.personDto.weight);
        }

      } else if (dependents[actualIndex.current]?.isError) {
        if (dependents[actualIndex.current].response?.Message) {
          dispatch(addSweetAlert({
            type: 'info',
            title: dependents[actualIndex.current].response?.Message,
            content: dependents[actualIndex.current].response?.Detail,
            showCancel: false,
            onConfirmLabel: 'Continuar',
            onConfirm: () => {
              dispatch(closeSweetAlert());
            },
          }));
        }
        formMethods.setValue(`insuranceDependants[${actualIndex.current}].name`, '');
        formMethods.setValue(`insuranceDependants[${actualIndex.current}].lastName`, '');
        formMethods.setValue(`insuranceDependants[${actualIndex.current}].birthDate`, null);
        formMethods.setValue(`insuranceDependants[${actualIndex.current}].gender`, '');
        formMethods.setValue(`insuranceDependants[${actualIndex.current}].nacionality`, '');
        formMethods.setValue(`insuranceDependants[${actualIndex.current}].height`, '');
        formMethods.setValue(`insuranceDependants[${actualIndex.current}].weight`, '');
      }
    }
  }, [dependentsIdentification]);

  const handleSearchIdentification = (index) => {
    const formValues = formMethods.getValues();
    const identification = formValues?.insuranceDependants[index]?.identification;
    if (identification) {
      if (isValidId(identification)) {
        actualIndex.current = index;
        dispatch(requestDependentIdentification(identification, index, productId));
      } else {
        dispatch(addSweetAlert({
          type: 'warning',
          title: Constants.MESSAGE_INVALID_IDENTIFICATION,
          showCancel: false,
          onConfirmLabel: 'Aceptar',
          onConfirm: () => {
            dispatch(closeSweetAlert());
          },
        }));
      }
    }
  };

  const checkErrorProperty = (index, property) => {
    if (formErrors?.insuranceDependants
      && formErrors?.insuranceDependants[index]) {
      return formErrors.insuranceDependants[index]?.hasOwnProperty(`${property}`);
    }
    return false;
  };

  const getMessage = (index, property) => {
    if (formErrors?.insuranceDependants
      && formErrors?.insuranceDependants[index]) {
      return formErrors?.insuranceDependants[index][property]?.message;
    }
    return '';
  };

  const findRolName = (index) => {
    const currentRol = roleSelector?.find((rol) => rol.id === dependants[index]?.rol);
    return currentRol?.description;
  };

  const getRoles = (isOther) => {
    if (isOther) {
      return relationshipSelector;
    }
    return roleSelector;
  };

  // set values.
  useEffect(() => {
    if (relationshipSelector && roleSelector) {
      if (fields?.length > 0) {
        fields.map((item, index) => formMethods.setValue(
          `insuranceDependants[${index}].relationshipBeneficiary`,
          dependants[index]?.rol === '3' ? '' : dependants[index].rol,
        ));
      }
    }
    if (typeIdentificationSelector) {
      fields.map((item, index) => formMethods.setValue(
        `insuranceDependants[${index}].identificationType`,
        Constants.DEFAULT_VALUE_TYPE_IDENTIFICATION,
      ));
    }
  }, [dependants, fields, relationshipSelector,
    roleSelector, formMethods.setValue, typeIdentificationSelector]);

  const onChangeIdentification = (event, position) => {
    resetFormApplicationValues(formMethods.setValue, formMethods.clearErrors,
      ResetApplicationForm.DependantFields, position);
    return event.target?.value;
  };

  return (
    <>
      {fields?.map((field, index) => {
        const relationship = getRoles(dependants[index]?.rol === '3');
        return (
          <CustomPanel key={field.id} withoutTitle subtitle={findRolName(index)}>
            <Grid container direction="row" spacing={3}>
              <Grid item md={4} xs={12}>
                <FormControl
                  className={classes.formControl}
                  fullWidth
                  error={checkErrorProperty(index, 'relationshipBeneficiary')}
                >
                  <InputLabel id="id-relationship">RELACIÓN CON TITULAR</InputLabel>
                  <Controller
                    name={`insuranceDependants[${index}].relationshipBeneficiary`}
                    control={formMethods.control}
                    defaultValue=""
                    render={({ field }) => (
                      <OutlinedSelect {...field} labelId="id-relationship" label="RELACIÓN CON TITULAR" inputProps={{ readOnly: dependants[index]?.rol !== '3' }}>
                        <MenuItem value="">
                          <em>Seleccione..</em>
                        </MenuItem>
                        {
                          relationship?.map((relation) => (
                            <MenuItem
                              key={relation.id}
                              value={relation.id}
                            >
                              {relation.description}

                            </MenuItem>
                          ))
                        }
                      </OutlinedSelect>
                    )}
                  />
                  <FormHelperText>{getMessage(index, 'relationshipBeneficiary')}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item md={4} xs={12}>
                <FormControl
                  className={classes.formControl}
                  fullWidth
                  error={checkErrorProperty(index, 'identificationType')}
                >
                  <InputLabel id="id-type-identification">TIPO DE IDENTIFICACIÓN</InputLabel>
                  <Controller
                    name={`insuranceDependants[${index}].identificationType`}
                    control={formMethods.control}
                    defaultValue=""
                    render={({ field }) => (
                      <OutlinedSelect {...field} labelId="id-type-identification" label="TIPO DE IDENTIFICACIÓN" readOnly={editionMode}>
                        <MenuItem value="">
                          <em>Seleccione...</em>
                        </MenuItem>
                        {
                          typeIdentificationSelector?.map((rol) => (
                            <MenuItem
                              key={rol.id}
                              value={rol.id}
                            >
                              {rol.description}
                            </MenuItem>
                          ))
                        }
                      </OutlinedSelect>
                    )}
                  />
                  <FormHelperText>{getMessage(index, 'identificationType')}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item md={4} xs={12}>
                <Controller
                  name={`insuranceDependants[${index}].identification`}
                  control={formMethods.control}
                  defaultValue={fields[index].identification}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      className={classes.formControl}
                      label="IDENTIFICACIÓN"
                      autoComplete="off"
                      onChange={(event) => { field.onChange(onChangeIdentification(event, index)); }}
                      error={checkErrorProperty(index, 'identification')}
                      helperText={getMessage(index, 'identification')}
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Buscar" arrow>
                            <IconButton
                              aria-label="search"
                              size="small"
                              onClick={() => handleSearchIdentification(index)}
                              disabled={editionMode}
                            >
                              <SearchIcon />
                            </IconButton>
                          </Tooltip>
                        ),
                        readOnly: editionMode,
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={3}>
              <Grid item md={6} xs={12}>
                <Controller
                  name={`insuranceDependants[${index}].name`}
                  control={formMethods.control}
                  defaultValue={fields[index].name}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      className={classes.formControl}
                      label="NOMBRES"
                      autoComplete="off"
                      InputProps={{ readOnly: isDisableInputs[index].isDisable }}
                      error={checkErrorProperty(index, 'name')}
                      helperText={getMessage(index, 'name')}
                    />
                  )}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <Controller
                  name={`insuranceDependants[${index}].lastName`}
                  control={formMethods.control}
                  defaultValue={fields[index].name}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      className={classes.formControl}
                      label="APELLIDOS"
                      autoComplete="off"
                      InputProps={{ readOnly: isDisableInputs[index].isDisable }}
                      error={checkErrorProperty(index, 'lastName')}
                      helperText={getMessage(index, 'lastName')}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={3}>
            <Grid item md={6} xs={12}>
                <Controller
                  name={`insuranceDependants[${index}].height`}
                  control={formMethods.control}
                  defaultValue={fields[index].height}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      className={classes.formControl}
                      label="ALTURA mtr"
                      autoComplete="off"
                      type="number"
                      inputProps={{ min: '0', step: '0.01', max: '3' }}
                      error={checkErrorProperty(index, 'height')}
                      helperText={getMessage(index, 'height')}
                      placeholder="1.82"
                    />
                  )}
                />
            </Grid>
              <Grid item md={6} xs={12}>
                <Controller
                  name={`insuranceDependants[${index}].weight`}
                  control={formMethods.control}
                  defaultValue={fields[index].weight}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      className={classes.formControl}
                      label="PESO Kg"
                      autoComplete="off"
                      type="number"
                      inputProps={{ min: '0', step: '1', max: '200' }}
                      error={checkErrorProperty(index, 'weight')}
                      helperText={getMessage(index, 'weight')}
                      placeholder="65"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={3}>
              <Grid item md={6} xs={12}>
                <FormControl
                  className={classes.formControl}
                  fullWidth
                  error={checkErrorProperty(index, 'gender')}
                >
                  <InputLabel id="id-gender">SEXO</InputLabel>
                  <Controller
                    name={`insuranceDependants[${index}].gender`}
                    control={formMethods.control}
                    defaultValue=""
                    render={({ field }) => (
                      <OutlinedSelect {...field} labelId="id-gender" fullWidth label="SEXO" inputProps={{ readOnly: isDisableInputs[index].isDisable }}>
                        {
                          gens?.map((gen) => (
                            <MenuItem
                              key={gen.id}
                              value={gen.id}
                            >
                              {gen.description}
                            </MenuItem>
                          ))
                        }
                      </OutlinedSelect>
                    )}
                  />
                  <FormHelperText>{getMessage(index, 'gender')}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item md={6} xs={12}>
                <Controller
                  name={`insuranceDependants[${index}].birthDate`}
                  control={formMethods.control}
                  defaultValue={fields[index].birthDate}
                  render={({ field }) => (
                    <BrokerDatePicker
                      {...field}
                      readOnly={isDisableInputs[index].isDisable}
                      disableFuture
                      className={classes.formControl}
                      fullWidth
                      format={PICKER_FORMAT_DATE}
                      margin="normal"
                      label="FECHA NACIMIENTO"
                      InputProps={{ readOnly: isDisableInputs[index].isDisable }}
                      error={checkErrorProperty(index, 'birthDate')}
                      helperText={getMessage(index, 'birthDate')}
                    />
                  )}
                />
              </Grid>

            </Grid>
            <Grid container direction="row" spacing={3}>
            <Grid item md={6} xs={12}>
                {
                  (
                      <FormControl
                        className={classes.formControl}
                        fullWidth
                        error={checkErrorProperty(index, 'nacionality')}
                      >
                        <InputLabel id="country-select">NACIONALIDAD</InputLabel>
                        <Controller
                          name={`insuranceDependants[${index}].nacionality`}
                          control={formMethods.control}
                          defaultValue={Constants.DEFAULT_VALUE_NACIONALITY}
                          render={({ field: { ref, ...fieldProps } }) => (
                            <OutlinedSelect {...fieldProps} inputRef={ref} labelId="country-select" label="NACIONALIDAD">
                              <MenuItem value="">
                                <em>Seleccione...</em>
                              </MenuItem>
                              {countries?.map((b) => (
                                b.id !== '0' && (
                                  <MenuItem
                                    key={b.id}
                                    value={b.description}
                                  >
                                    {b.description}
                                  </MenuItem>
                                )
                              ))}
                            </OutlinedSelect>
                          )}
                        />
                        <FormHelperText>{getMessage(index, 'nacionality')}</FormHelperText>
                      </FormControl>
                    )
                }
              </Grid>
            <Grid item md={6} xs={12}>
                {
                  dependentsIdentification.dependents[index].found
                    ? (
                      <Controller
                        name={`insuranceDependants[${index}].birthPlace`}
                        control={formMethods.control}
                        defaultValue={fields[index].birthPlace}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            className={classes.formControl}
                            label="LUGAR NACIMIENTO"
                            autoComplete="off"
                            error={checkErrorProperty(index, 'birthPlace')}
                            helperText={getMessage(index, 'birthPlace')}
                          />
                        )}
                      />
                    )
                    : (
                      <FormControl
                        className={classes.formControl}
                        fullWidth
                        error={checkErrorProperty(index, 'birthPlace')}
                      >
                        <InputLabel id="province-select">LUGAR NACIMIENTO</InputLabel>
                        <Controller
                          name={`insuranceDependants[${index}].birthPlace`}
                          control={formMethods.control}
                          defaultValue="Ecuador"
                          render={({ field: { ref, ...fieldProps } }) => (
                            <OutlinedSelect {...fieldProps} inputRef={ref} labelId="province-select" label="LUGAR NACIMIENTO">
                              <MenuItem value="">
                                <em>Seleccione...</em>
                              </MenuItem>
                              {countries?.map((b) => (
                                b.id !== '0' && (
                                  <MenuItem
                                    key={b.id}
                                    value={b.description}
                                  >
                                    {b.description}
                                  </MenuItem>
                                )
                              ))}
                            </OutlinedSelect>
                          )}
                        />
                        <FormHelperText>{getMessage(index, 'birthPlace')}</FormHelperText>
                      </FormControl>
                    )
                }
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={3}>
              <Grid item xs={12}>
                <Typography className={classes.formControl}>
                  Condiciones Médicas
                </Typography>
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={3}>
              {isEnabledCovidParam && (
                <Grid item xs={12}>
                  <Covid19Questions
                    fieldName={`insuranceDependants[${index}]`}
                    hasCovid={hasCovid}
                    dependantIndex={index}
                    hasResultPositive={dependantsValues[index]?.QuestionCovid19ResultPositive}
                  />
                </Grid>
              )}
            </Grid>
            <Grid container direction="row" spacing={3}>
              <MedicalConditions
                fieldArrayName={`insuranceDependants[${index}].medicalConditions`}
                medicalConditionsValues={dependantsValues?.length > 0
                  ? dependantsValues[index].InsuranceDependantMedicalConditions
                  : []}
                hasCovid={enableCovidMedicalCondition[index].hasCovidEnabled}
              />
            </Grid>
          </CustomPanel>
        );
      })}
    </>
  );
}

Dependents.propTypes = {
  dependants: PropTypes.arrayOf(PropTypes.object).isRequired,
  isEnabledCovidParam: PropTypes.bool.isRequired,
  editionMode: PropTypes.bool.isRequired,
};

export default memo(Dependents);
